import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';

import { FormattedMessage } from '../../../../util/reactIntl';
import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import {
  Avatar,
  InlineTextButton,
  LinkedLogo,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  NamedLink,
} from '../../../../components';

import TopbarSearchForm from '../TopbarSearchForm/TopbarSearchForm';
import IconHeart from '../../../../components/IconHeart/IconHeart';

import { ReactComponent as InboxIcon } from '../../../../assets/InboxIcon.svg';
import { resolveLogoLink } from '../../../../util/logoLinks';
import { buildCategoryPathParams } from '../../../../util/search';

import css from './TopbarDesktop.module.css';

const splitIntoColumns = (items = [], columnCount = 3, options = {}) => {
  const reservePerColumn = Array.from({ length: columnCount }).map((_, index) => {
    const reserve = options?.reservePerColumn?.[index];
    return Number.isFinite(reserve) && reserve > 0 ? reserve : 0;
  });

  const createEmptyColumns = () => Array.from({ length: columnCount }).map(() => []);

  if (!Array.isArray(items) || items.length === 0) {
    return createEmptyColumns();
  }

  const columns = createEmptyColumns();
  let startIndex = 0;
  let remainingItems = items.length;

  for (let columnIndex = 0; columnIndex < columnCount; columnIndex += 1) {
    const reserveForColumn = reservePerColumn[columnIndex] || 0;
    const columnsRemaining = columnCount - columnIndex;
    const reserveRemaining = reservePerColumn
      .slice(columnIndex)
      .reduce((sum, value) => sum + value, 0);
    const totalSlots = remainingItems + reserveRemaining;
    const itemsPerColumn = Math.ceil(totalSlots / columnsRemaining);
    const itemsForColumn = itemsPerColumn - reserveForColumn;
    const sliceCount = Math.max(itemsForColumn, 0);
    const columnItems = items.slice(startIndex, startIndex + sliceCount);

    columns[columnIndex] = columnItems;
    startIndex += sliceCount;
    remainingItems -= sliceCount;
  }

  return columns;
};

const HEADER_CATEGORY_IDS = [
  'complete-systems',
  'rigs',
  'main-canopies',
  'reserves',
  'altimeters',
  'audibles',
  'helmets-and-equipment',
  'base',
];

const HamburgerIcon = ({ className }) => (
  <svg
    className={className}
    width="18"
    height="12"
    viewBox="0 0 18 12"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <g fillRule="evenodd">
      <rect width="18" height="2" rx="1" />
      <rect y="5" width="18" height="2" rx="1" />
      <rect y="10" width="18" height="2" rx="1" />
    </g>
  </svg>
);

const ArrowRightIcon = ({ className }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path
      d="M9 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      fill="none"
    />
  </svg>
);

const CategoryToggleIcon = ({ className, isOpen }) => (
  <svg
    className={className}
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path d="M3 8h10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path
      className={classNames(css.toggleVerticalLine, { [css.toggleVerticalLineOpen]: isOpen })}
      d="M3 8h10"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const ProfileMenu = ({ currentPage, currentUser, onLogout, showManageListingsLink, isOpen, onMouseEnter, onMouseLeave, onToggleActive }) => {
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    return currentPage === page || isAccountSettingsPage ? css.currentPage : null;
  };

  return (
    <div className={css.profileMenuWrapper} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <Menu contentPosition="left" isOpen={isOpen} onToggleActive={onToggleActive}>
        <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
          <Avatar className={css.avatar} user={currentUser} disableProfileLink />
        </MenuLabel>
        <MenuContent className={css.profileMenuContent}>
          {showManageListingsLink ? (
            <MenuItem key="ManageListingsPage">
              <NamedLink
                className={classNames(css.menuLink, currentPageClass('ManageListingsPage'))}
                name="ManageListingsPage"
              >
                <FormattedMessage id="TopbarDesktop.yourListingsLink" />
              </NamedLink>
            </MenuItem>
          ) : null}
          <MenuItem key="FavouritesPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('FavouritesPage'))}
              name="FavouritesPage"
            >
              <FormattedMessage id="TopbarDesktop.favouritesLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="WishlistPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('WishlistPage'))}
              name="WishlistPage"
            >
              <FormattedMessage id="TopbarDesktop.wishlistLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="ProfileSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('ProfileSettingsPage'))}
              name="ProfileSettingsPage"
            >
              <FormattedMessage id="TopbarDesktop.profileSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="AccountSettingsPage">
            <NamedLink
              className={classNames(css.menuLink, currentPageClass('AccountSettingsPage'))}
              name="AccountSettingsPage"
            >
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </NamedLink>
          </MenuItem>
          <MenuItem key="logout">
            <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
              <FormattedMessage id="TopbarDesktop.logout" />
            </InlineTextButton>
          </MenuItem>
        </MenuContent>
      </Menu>
    </div>
  );
};

const TopbarDesktop = props => {
  const {
    className,
    config,
    currentUser,
    currentPage,
    rootClassName,
    logoLink,
    notificationCount = 0,
    intl,
    isAuthenticated,
    onLogout,
    onSearchSubmit,
    onAiSearchSubmit,
    initialSearchFormValues = {},
    showSearchForm,
    showCreateListingsLink,
    inboxTab,
    onCategorySelect,
    buildSearchUrl,
    onManageDisableScrolling,
    autoOpenBuyMenu = false,
    onAutoOpenBuyMenuHandled,
    searchPlaceholder,
  } = props;

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState([]);
  const [overlayMetrics, setOverlayMetrics] = useState(null);
  const [dropdownTop, setDropdownTop] = useState(null);
  const [dropdownPortalNode, setDropdownPortalNode] = useState(null);
  const dropdownRef = useRef(null);
  const bottomRowRef = useRef(null);
  const headerRef = useRef(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileCloseTimer = useRef(null);
  const profileOpenTimer = useRef(null);

  const cancelProfileClose = useCallback(() => {
    if (profileCloseTimer.current) {
      clearTimeout(profileCloseTimer.current);
      profileCloseTimer.current = null;
    }
  }, []);

  const cancelProfileOpen = useCallback(() => {
    if (profileOpenTimer.current) {
      clearTimeout(profileOpenTimer.current);
      profileOpenTimer.current = null;
    }
  }, []);

  const scheduleProfileClose = useCallback(() => {
    cancelProfileClose();
    cancelProfileOpen();
    profileCloseTimer.current = setTimeout(() => {
      setIsProfileMenuOpen(false);
      profileCloseTimer.current = null;
    }, 200);
  }, [cancelProfileClose, cancelProfileOpen]);

  const openProfileMenu = useCallback(() => {
    cancelProfileClose();
    cancelProfileOpen();
    const doOpen = () => {
      setIsProfileMenuOpen(true);
      setActiveDropdown(null);
      setExpandedCategories([]);
    };
    if (isProfileMenuOpen) {
      doOpen();
    } else {
      profileOpenTimer.current = setTimeout(doOpen, 200);
    }
  }, [cancelProfileClose, cancelProfileOpen, isProfileMenuOpen]);

  useEffect(
    () => () => {
      cancelProfileClose();
      cancelProfileOpen();
    },
    [cancelProfileClose, cancelProfileOpen]
  );

  const resolvedLogoLink = resolveLogoLink(logoLink);

  const updateDropdownPosition = useCallback(() => {
    if (!headerRef.current) {
      return;
    }
    const { bottom } = headerRef.current.getBoundingClientRect();
    setDropdownTop(prev => (prev !== bottom ? bottom : prev));
  }, []);

  const updateDropdownMetrics = useCallback(() => {
    if (!dropdownRef.current || !headerRef.current) {
      return;
    }

    const headerRect = headerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const overlayHeight = Math.max(viewportHeight - headerRect.bottom, 0);

    setDropdownTop(prev => (prev !== headerRect.bottom ? headerRect.bottom : prev));
    setOverlayMetrics({ top: headerRect.bottom, height: overlayHeight });
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      setDropdownPortalNode(document.body);
    }
  }, []);

  useEffect(() => {
    if (autoOpenBuyMenu) {
      updateDropdownPosition();
      setActiveDropdown('all');
      if (onAutoOpenBuyMenuHandled) {
        onAutoOpenBuyMenuHandled();
      }
    }
  }, [autoOpenBuyMenu, onAutoOpenBuyMenuHandled, updateDropdownPosition]);

  const [closingDropdown, setClosingDropdown] = useState(false);

  const finishClose = useCallback(() => {
    setActiveDropdown(null);
    setExpandedCategories([]);
    setClosingDropdown(false);
    if (onAutoOpenBuyMenuHandled) {
      onAutoOpenBuyMenuHandled();
    }
    setOverlayMetrics(null);
    setDropdownTop(null);
  }, [onAutoOpenBuyMenuHandled]);

  const closeMenus = useCallback(() => {
    if (activeDropdown === null) return;
    setClosingDropdown(true);
  }, [activeDropdown]);

  const handleDropdownAnimationEnd = useCallback(() => {
    if (closingDropdown) {
      finishClose();
    }
  }, [closingDropdown, finishClose]);

  useEffect(() => {
    const handleEsc = event => {
      if (event.key === 'Escape') {
        closeMenus();
      }
    };
    if (activeDropdown !== null) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => window.removeEventListener('keydown', handleEsc);
  }, [activeDropdown, closeMenus]);

  const isDropdownOpen = activeDropdown !== null;

  useEffect(() => {
    if (onManageDisableScrolling) {
      onManageDisableScrolling('TopbarDesktopBuyDropdown', isDropdownOpen);
      return () => onManageDisableScrolling('TopbarDesktopBuyDropdown', false);
    }
  }, [isDropdownOpen, onManageDisableScrolling]);

  useEffect(() => {
    if (!isDropdownOpen) {
      return undefined;
    }

    const handlePointerDown = event => {
      const dropdownNode = dropdownRef.current;
      const bottomRowNode = bottomRowRef.current;
      const clickedInsideDropdown = dropdownNode?.contains(event.target);
      const clickedBottomRow = bottomRowNode?.contains(event.target);

      if (clickedInsideDropdown || clickedBottomRow) {
        return;
      }

      closeMenus();
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [isDropdownOpen, closeMenus]);

  const marketplaceName = config.marketplaceName;

  const categories = useMemo(() => config?.categoryConfiguration?.categories || [], [
    config?.categoryConfiguration?.categories,
  ]);
  const categoryLevelKeys = config?.categoryConfiguration?.categoryLevelKeys || [];

  const headerCategories = useMemo(
    () => HEADER_CATEGORY_IDS.map(id => categories.find(c => c.id === id)).filter(Boolean),
    [categories]
  );

  const hoverCloseTimer = useRef(null);
  const hoverOpenTimer = useRef(null);

  const cancelHoverClose = useCallback(() => {
    if (hoverCloseTimer.current) {
      clearTimeout(hoverCloseTimer.current);
      hoverCloseTimer.current = null;
    }
  }, []);

  const cancelHoverOpen = useCallback(() => {
    if (hoverOpenTimer.current) {
      clearTimeout(hoverOpenTimer.current);
      hoverOpenTimer.current = null;
    }
  }, []);

  const scheduleHoverClose = useCallback(() => {
    cancelHoverClose();
    cancelHoverOpen();
    hoverCloseTimer.current = setTimeout(() => {
      closeMenus();
      hoverCloseTimer.current = null;
    }, 150);
  }, [cancelHoverClose, cancelHoverOpen, closeMenus]);

  useEffect(() => {
    return () => {
      cancelHoverClose();
      cancelHoverOpen();
    };
  }, [cancelHoverClose, cancelHoverOpen]);

  const openDropdown = useCallback(
    mode => {
      cancelHoverClose();
      cancelHoverOpen();
      setIsProfileMenuOpen(false);
      if (activeDropdown !== mode) {
        const doOpen = () => {
          updateDropdownPosition();
          setActiveDropdown(mode);
          setExpandedCategories([]);
        };
        if (activeDropdown !== null) {
          doOpen();
        } else {
          hoverOpenTimer.current = setTimeout(doOpen, 200);
        }
      }
    },
    [activeDropdown, cancelHoverClose, cancelHoverOpen, updateDropdownPosition]
  );

  const toggleDropdown = useCallback(
    mode => {
      cancelHoverClose();
      if (activeDropdown === mode) {
        closeMenus();
      } else {
        updateDropdownPosition();
        setActiveDropdown(mode);
        setExpandedCategories([]);
      }
    },
    [activeDropdown, cancelHoverClose, closeMenus, updateDropdownPosition]
  );

  const handleCategoryToggle = useCallback(categoryId => {
    setExpandedCategories(prev =>
      prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]
    );
  }, []);

  const handleCategorySelection = useCallback(
    (path = []) => {
      if (onCategorySelect) {
        onCategorySelect(buildCategoryPathParams(path, categoryLevelKeys), path);
      }
      closeMenus();
    },
    [categoryLevelKeys, onCategorySelect, closeMenus]
  );

  const categoryUrl = (path = []) =>
    buildSearchUrl(buildCategoryPathParams(path, categoryLevelKeys));

  // Modifier clicks fall through to the browser (new tab); plain clicks stay SPA.
  const handleCategoryLinkClick = (event, path) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    handleCategorySelection(path);
  };

  const dropdownItems = useMemo(() => {
    if (activeDropdown === null) return [];
    if (activeDropdown === 'all') return categories;

    const selectedCategory = categories.find(c => c.id === activeDropdown);
    if (!selectedCategory) return categories;
    return selectedCategory.subcategories || [];
  }, [activeDropdown, categories]);

  const activeParentCategory = useMemo(() => {
    if (activeDropdown === null || activeDropdown === 'all') return null;
    return categories.find(c => c.id === activeDropdown) || null;
  }, [activeDropdown, categories]);

  const dropdownColumns = useMemo(
    () => splitIntoColumns(dropdownItems, 3, { reservePerColumn: [1, 0, 0] }),
    [dropdownItems]
  );

  const renderCategoryNode = (category, depth = 0, path = []) => {
    const hasChildren = Array.isArray(category?.subcategories) && category.subcategories.length > 0;
    const isExpanded = expandedCategories.includes(category.id);
    const nextPath = [...path, category];

    const toggleCategory = event => {
      event.preventDefault();
      event.stopPropagation();
      handleCategoryToggle(category.id);
    };

    return (
      <li key={category.id} className={css.categoryNode}>
        <div
          className={css.categoryRow}
          style={depth > 0 ? { paddingLeft: `${12 + depth * 20}px` } : undefined}
        >
          <a
            href={categoryUrl(nextPath)}
            className={classNames(css.categorySelectButton, {
              [css.categorySelectButtonChild]: depth > 0,
            })}
            onClick={event => handleCategoryLinkClick(event, nextPath)}
          >
            {category.name}
          </a>
          {hasChildren ? (
            <button
              type="button"
              className={css.categoryToggleButton}
              onClick={toggleCategory}
              aria-expanded={isExpanded}
              aria-label={intl.formatMessage(
                {
                  id: isExpanded
                    ? 'TopbarDesktop.collapseCategory'
                    : 'TopbarDesktop.expandCategory',
                },
                { category: category.name }
              )}
            >
              <CategoryToggleIcon className={css.plusMinusIcon} isOpen={isExpanded} />
            </button>
          ) : null}
        </div>
        {hasChildren ? (
          <div
            className={classNames(css.childListWrapper, { [css.childListWrapperOpen]: isExpanded })}
          >
            <ul className={css.categoryList}>
              {category.subcategories.map(subcategory =>
                renderCategoryNode(subcategory, depth + 1, nextPath)
              )}
            </ul>
          </div>
        ) : null}
      </li>
    );
  };

  const parentPath = activeParentCategory ? [activeParentCategory] : [];

  const renderDropdownColumns = () =>
    dropdownColumns.map((column, columnIndex) => (
      <div
        key={`category-column-${columnIndex}`}
        className={classNames(css.categoryColumn, {
          [css.categoryColumnBorder]: columnIndex > 0,
        })}
      >
        <ul className={css.categoryColumnList}>
          {columnIndex === 0 ? (
            <li key="all-link" className={css.categoryNode}>
              <div className={css.allListingsRow}>
                <a
                  href={categoryUrl(parentPath)}
                  className={css.categorySelectButton}
                  onClick={event => handleCategoryLinkClick(event, parentPath)}
                >
                  {activeParentCategory ? (
                    <FormattedMessage
                      id="TopbarDesktop.allCategoryListings"
                      values={{ category: activeParentCategory.name }}
                    />
                  ) : (
                    <FormattedMessage id="TopbarDesktop.allListings" />
                  )}
                </a>
                <ArrowRightIcon className={css.arrowIcon} />
              </div>
            </li>
          ) : null}
          {column.map(category => renderCategoryNode(category, 0, parentPath))}
        </ul>
      </div>
    ));

  useEffect(() => {
    if (!isDropdownOpen) {
      setOverlayMetrics(null);
      setDropdownTop(null);
      return () => {};
    }

    updateDropdownMetrics();

    const handleResize = () => updateDropdownMetrics();
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize, true);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize, true);
    };
  }, [isDropdownOpen, updateDropdownMetrics]);

  useEffect(() => {
    if (isDropdownOpen) {
      const id = window.requestAnimationFrame(() => updateDropdownMetrics());
      return () => window.cancelAnimationFrame(id);
    }
  }, [isDropdownOpen, expandedCategories, updateDropdownMetrics]);

  const overlayStyle =
    isDropdownOpen && overlayMetrics
      ? {
          top: `${overlayMetrics.top}px`,
          height: `${overlayMetrics.height}px`,
        }
      : undefined;

  const searchFormMaybe = showSearchForm ? (
    <TopbarSearchForm
      className={css.searchForm}
      desktopInputRoot={css.searchInputRoot}
      onSubmit={onSearchSubmit}
      initialValues={initialSearchFormValues}
      appConfig={config}
      onAiSearch={onAiSearchSubmit}
      placeholder={searchPlaceholder}
    />
  ) : (
    <div className={css.searchPlaceholder} />
  );

  // Auth state is known during SSR (see server/dataLoader.js), so server and
  // client render the same controls: no logged-out flash, no hydration mismatch.
  const accountControls = isAuthenticated ? (
    <div className={css.accountControls}>
      <NamedLink className={css.iconLink} name="FavouritesPage">
        <IconHeart className={css.favouriteIcon} />
      </NamedLink>
      <NamedLink className={css.iconLink} name="InboxPage" params={{ tab: inboxTab }}>
        <InboxIcon className={css.inboxIcon} />
        {notificationCount > 0 ? <span className={css.notificationDot} /> : null}
      </NamedLink>
      <ProfileMenu
        currentPage={currentPage}
        currentUser={currentUser}
        onLogout={onLogout}
        showManageListingsLink={showCreateListingsLink}
        isOpen={isProfileMenuOpen}
        onToggleActive={val => {
          cancelProfileClose();
          cancelProfileOpen();
          if (val) setIsProfileMenuOpen(true);
        }}
        onMouseEnter={openProfileMenu}
        onMouseLeave={scheduleProfileClose}
      />
      <NamedLink className={css.sellButton} name="NewListingPage">
        <FormattedMessage id="TopbarDesktop.sell" />
      </NamedLink>
    </div>
  ) : (
    <div className={css.accountControls}>
      <NamedLink className={css.loginButton} name="LoginPage">
        <FormattedMessage id="TopbarDesktop.loginButton" />
      </NamedLink>
      {/* Guests land on the /sell marketing page first; on it (or signed in) the button
          starts the listing flow directly. */}
      <NamedLink
        className={css.sellButton}
        name={currentPage === 'SellPage' ? 'NewListingPage' : 'SellPage'}
      >
        <FormattedMessage id="TopbarDesktop.sell" />
      </NamedLink>
    </div>
  );

  const dropdownStyle = dropdownTop != null ? { top: `${dropdownTop}px` } : undefined;

  const dropdownContent =
    isDropdownOpen && dropdownPortalNode
      ? createPortal(
          <div
            className={classNames(css.buyDropdown, { [css.buyDropdownClosing]: closingDropdown })}
            ref={dropdownRef}
            style={dropdownStyle}
            onMouseEnter={cancelHoverClose}
            onMouseLeave={scheduleHoverClose}
            onAnimationEnd={handleDropdownAnimationEnd}
          >
            <div className={css.buyDropdownInner}>
              <div className={css.categoryColumns}>{renderDropdownColumns()}</div>
            </div>
          </div>,
          dropdownPortalNode
        )
      : null;

  return (
    <>
      <header ref={headerRef} className={classNames(rootClassName || css.root, className)}>
        <div className={css.inner}>
          <div className={css.topRow}>
            <div className={css.topLeft}>
              <LinkedLogo
                className={css.logoLink}
                layout="desktop"
                alt={intl.formatMessage({ id: 'TopbarDesktop.logo' }, { marketplaceName })}
                linkToExternalSite={resolvedLogoLink}
              />
            </div>
            <div className={css.searchWrapper}>{searchFormMaybe}</div>
            <div className={css.topRight}>{accountControls}</div>
          </div>

          <div className={css.bottomRow} ref={bottomRowRef} onMouseLeave={scheduleHoverClose}>
            <div className={css.categoryTabs}>
              {headerCategories.map(category => (
                <a
                  key={category.id}
                  href={categoryUrl([category])}
                  className={classNames(css.categoryTab, {
                    [css.categoryTabActive]: activeDropdown === category.id,
                  })}
                  onClick={event => handleCategoryLinkClick(event, [category])}
                  onMouseEnter={() => openDropdown(category.id)}
                >
                  {category.name}
                </a>
              ))}
              <a
                href={categoryUrl([])}
                className={classNames(css.categoryTab, css.allCategoriesTab, {
                  [css.categoryTabActive]: activeDropdown === 'all',
                })}
                onClick={event => handleCategoryLinkClick(event, [])}
                onMouseEnter={() => openDropdown('all')}
              >
                <HamburgerIcon className={css.hamburgerIcon} />
                <FormattedMessage id="TopbarDesktop.allCategories" />
              </a>
            </div>
          </div>
        </div>

        {isDropdownOpen ? (
          <div
            className={classNames(css.overlay, { [css.overlayClosing]: closingDropdown })}
            style={overlayStyle}
            onClick={closeMenus}
          />
        ) : null}
      </header>
      {dropdownContent}
    </>
  );
};

export default TopbarDesktop;
